import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { selectionQuery, isRolling } from '../lib/analyticsDates';

export const useAnalyticsData = (selection, onExpired) => {
  const query = selectionQuery(selection);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const request = useRef({id: 0, controller: null});
  const moreController = useRef(null);

  const refresh = useCallback(async () => {
    request.current.controller?.abort();
    moreController.current?.abort();
    setLoadingMore(false);
    const controller = new AbortController();
    const id = ++request.current.id;
    request.current.controller = controller;
    setBusy(true); setError('');
    try {
      let stats, activity, traffic, experiment, windowQuery = query;
      if (isRolling(selection)) {
        // Let the server choose one precise clock instant, then use it for
        // every other view, pagination request, and export in this snapshot.
        stats = await api(`/analytics/stats?${query}`, {signal: controller.signal});
        if (controller.signal.aborted || id !== request.current.id) return;
        const params = new URLSearchParams(query);
        params.set('as_of', stats.selection.end_at);
        windowQuery = params.toString();
        [activity, traffic, experiment] = await Promise.all(
          ['activity', 'traffic', 'experiment'].map(path => api(`/analytics/${path}?${windowQuery}`, {signal: controller.signal}))
        );
      } else {
        [stats, activity, traffic, experiment] = await Promise.all(
          ['stats', 'activity', 'traffic', 'experiment'].map(path => api(`/analytics/${path}?${query}`, {signal: controller.signal}))
        );
      }
      if (controller.signal.aborted || id !== request.current.id) return;
      setData({key: query, windowQuery, stats, activity, traffic, experiment});
    } catch (error) {
      if (controller.signal.aborted || id !== request.current.id) return;
      if (error.status === 401) onExpired(); else setError(error.message);
    } finally { if (id === request.current.id && !controller.signal.aborted) setBusy(false); }
  }, [query, onExpired, selection]);

  useEffect(() => {
    const currentRequest = request.current;
    refresh();
    const interval = setInterval(() => { if (!document.hidden) refresh(); }, 30000);
    return () => { currentRequest.controller?.abort(); moreController.current?.abort(); ++currentRequest.id; clearInterval(interval); };
  }, [refresh]);

  const loadMore = async () => {
    if (!data || data.key !== query || busy || loadingMore) return;
    const controller = new AbortController();
    moreController.current = controller;
    const requestId = request.current.id;
    setLoadingMore(true);
    try {
      const next = await api(`/analytics/activity?${data.windowQuery}&offset=${data.activity.items.length}`, {signal: controller.signal});
      if (controller.signal.aborted || requestId !== request.current.id) return;
      setData(previous => previous?.key === query ? {...previous, activity: {...next,
        items: [...previous.activity.items, ...next.items.filter(item => !previous.activity.items.some(old => old.event_id === item.event_id))]}} : previous);
    } catch (error) {
      if (!controller.signal.aborted) { if (error.status === 401) onExpired(); else setError(error.message); }
    } finally { if (!controller.signal.aborted) setLoadingMore(false); }
  };
  const matchesSelection = data?.key === query;
  const pendingSelection = !matchesSelection && !error;
  return {data: matchesSelection ? data : null, busy: busy || pendingSelection, error, setError, refresh, loadMore, loadingMore};
};